import { Box, Stack } from "@mui/material";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { parseAsString, parseAsStringEnum, useQueryState } from "nuqs";

import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import { getProjectStateOptions } from "Forms/Utils/EnumOptions";
import {
  ProjectState,
  SortableColumn,
  SortOrder,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useProjectsListQuery } from "Projects/Api/Queries/useProjectsListQuery";
import { ClientFilterSelect } from "Projects/Components/ClientFilterSelect";
import {
  PROJECTS_SORTABLE_COLUMNS,
  useProjectsTableColumns,
} from "Projects/Components/useProjectsTableColumns";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlDropdown } from "Shared/Inputs/BlDropdown";
import type { BlOption } from "Shared/Inputs/BlOption";
import { TableSearchInput } from "./TableSearchInput";

const sortColumnMap: Partial<Record<string, SortableColumn>> = {
  name: SortableColumn.Name,
  clientName: SortableColumn.ClientName,
  projectState: SortableColumn.ProjectState,
};

export const ProjectsTable = (): React.ReactNode => {
  const { t } = useResource();

  const [selectedClient, setSelectedClient] = useQueryState(
    "client",
    parseAsString,
  );
  const [selectedStatus, setSelectedStatus] = useQueryState(
    "status",
    parseAsStringEnum<ProjectState>(Object.values(ProjectState)),
  );

  const {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    resetPage,
    sorting,
    onSortingChange,
  } = useTableSearchParams({
    validSortIds: [...PROJECTS_SORTABLE_COLUMNS],
  });

  const sortBy = sorting[0] ? sortColumnMap[sorting[0].id] : undefined;
  const sortOrder = sorting[0]?.desc
    ? SortOrder.Descending
    : SortOrder.Ascending;

  const { data, isLoading } = useProjectsListQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    clientID: selectedClient || null,
    searchQuery: debouncedSearch || null,
    projectState: selectedStatus || null,
    sortBy,
    sortOrder,
  });

  const projects = data?.items ?? [];

  const statusOptions: BlOption[] = [
    { value: "", label: t(Resources.Dashboard.AllStatuses) },
    ...getProjectStateOptions(),
  ];

  const columns = useProjectsTableColumns();

  const table = useReactTable({
    data: projects,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: data?.totalCount ?? 0,
    state: {
      pagination: {
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
      sorting,
    },
    onPaginationChange,
    onSortingChange,
  });

  return (
    <>
      <Stack direction="row" spacing={Spacing.S}>
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            name="project-search"
            value={search}
            onChange={onSearchChange}
            placeholder={t(Resources.Dashboard.SearchProjectsPlaceholder)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <ClientFilterSelect
            value={selectedClient}
            onChange={(option) => {
              setSelectedClient((option?.value as string) || null);
              resetPage();
            }}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <BlDropdown
            name="status-filter"
            options={statusOptions}
            value={selectedStatus || ""}
            onChange={(value) => {
              setSelectedStatus(value ? (value as ProjectState) : null);
              resetPage();
            }}
            placeholder={t(Resources.Dashboard.Status)}
          />
        </Box>
      </Stack>

      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={data?.totalCount ?? 0}
      />
    </>
  );
};
